/* ===========================================================================
   PMC RENTAL PAGE
   Ported from reference/palen-rental-page.html <script> block.

   Four behaviours, all defensive, all no-ops if their markup is absent:
     1. Video facade   swap the poster for a YouTube iframe on click or key
     2. Sticky bar     tuck while the hero or the closer is on screen
     3. GA4 tracking   fire rental_cta_click both ways, gtag and dataLayer
     4. Ticker fit     under reduced motion, trim to whole phrases
     5. Delivery pick  report which step 1 delivery method was chosen

   Differences from the reference, all forced by Online Store 2.0:
     - The reference bound to #pmc-video and #pmc-bar. Ids are not safe in
       sections because a merchant can add a section twice, so this binds to
       [data-pmc-video] and [data-pmc-bar] and handles any number of them.
     - init() re-runs on shopify:section:load so sections keep working when
       they are re-rendered in the theme customizer.
     - Elements are marked once bound, so re-running never double-binds.
   =========================================================================== */
(function () {
  'use strict';

  var BOUND = 'pmcBound';

  /* -------------------------------------------------------------------------
     1. VIDEO FACADE
     The clickable element is a <div role="button">, never a <button>. Form
     controls apply their own intrinsic sizing and collapse when combined with
     aspect-ratio, which once rendered this player 124px wide.
  ------------------------------------------------------------------------- */
  function initVideo(root) {
    var videos = root.querySelectorAll('[data-pmc-video]');

    Array.prototype.forEach.call(videos, function (v) {
      if (v.dataset[BOUND]) { return; }
      v.dataset[BOUND] = '1';

      var id = v.getAttribute('data-pmc-video');
      if (!id) { return; }

      var played = false;

      function play() {
        if (played) { return; }
        played = true;

        var f = document.createElement('iframe');
        f.setAttribute('src', 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0');
        f.setAttribute('title', v.getAttribute('data-pmc-video-title') || 'Video');
        f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        f.setAttribute('allowfullscreen', '');

        v.innerHTML = '';
        v.appendChild(f);
        v.style.cursor = 'default';
        v.removeAttribute('role');
        v.removeAttribute('tabindex');
        v.removeAttribute('aria-label');
      }

      v.addEventListener('click', play);
      v.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          play();
        }
      });
    });
  }

  /* -------------------------------------------------------------------------
     2. STICKY MOBILE BAR
     Tucks while the hero or the closer is on screen. If IntersectionObserver
     is missing, or this script never runs at all, the bar simply stays
     visible, which is the safe failure mode.
  ------------------------------------------------------------------------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-pmc-bar]');
    if (!bar || bar.dataset[BOUND]) { return; }
    if (!('IntersectionObserver' in window)) { return; }

    var hero = document.querySelector('[data-pmc-hero]');
    var end = document.querySelector('[data-pmc-closer]');
    if (!hero && !end) { return; }

    bar.dataset[BOUND] = '1';

    var at = { top: !!hero, bottom: false };

    function sync() {
      bar.classList.toggle('is-down', at.top || at.bottom);
    }

    if (hero) {
      new IntersectionObserver(function (e) {
        at.top = e[0].isIntersecting;
        sync();
      }, { threshold: 0.2 }).observe(hero);
    }

    if (end) {
      new IntersectionObserver(function (e) {
        at.bottom = e[0].isIntersecting;
        sync();
      }, { threshold: 0.2 }).observe(end);
    }

    sync();
  }

  /* -------------------------------------------------------------------------
     3. GA4 TRACKING
     Fires BOTH ways so it works whether the store runs Google Tag Manager or
     gtag.js directly. A plain dataLayer.push does nothing under gtag.js, and
     gtag() does not exist under a pure GTM install, so we try each.

     NOTE FOR CALEB: register cta_location as a custom dimension in GA4 Admin
     the day this publishes. It is not retroactive.
  ------------------------------------------------------------------------- */
  function initTracking() {
    if (document.documentElement.dataset.pmcTracking) { return; }
    document.documentElement.dataset.pmcTracking = '1';

    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('[data-cta]') : null;
      if (!a) { return; }

      var payload = {
        cta_location: a.getAttribute('data-cta'),
        cta_text: (a.textContent || '').trim(),
        page_section: 'band_rental'
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'rental_cta_click', payload);
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'rental_cta_click' }, payload));
    });
  }

  /* -------------------------------------------------------------------------
     4. TICKER FIT
     Only runs when the visitor has asked for reduced motion. CSS stops the
     marquee in that case, which leaves a static strip clipped mid word by the
     .ticker's overflow. This trims it to the whole phrases that actually fit
     and centres them, so it locks in place on a clean boundary instead.

     CSS cannot do this: it has no way to ask how wide a phrase is. If this
     never runs, the CSS fallback still holds, a clipped one line blue bar, so
     failure degrades rather than breaks. The .ticker--fit class is added only
     after a successful trim, which is what keeps the two states separate.

     The strip is aria-hidden and decorative, and every claim in it appears
     elsewhere on the page, so hiding the tail costs nothing.
  ------------------------------------------------------------------------- */
  function initTickerFit() {
    var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var DOT = '\u2022';

    function fit() {
      var tickers = document.querySelectorAll('.pmc-rental .ticker');

      Array.prototype.forEach.call(tickers, function (t) {
        var run = t.querySelector('.ticker-run');
        if (!run) { return; }

        var items = Array.prototype.slice.call(run.children);
        if (!items.length) { return; }

        /* Always restore first. The preference can flip at runtime, and the
           marquee needs every span back or the -50% loop shows a gap. */
        items.forEach(function (el) { el.style.display = ''; });
        t.classList.remove('ticker--fit');
        if (!mq || !mq.matches) { return; }

        /* Add the class BEFORE measuring, not after. It tightens the phrase
           padding, so measuring first would size against the wide spacing and
           then apply the narrow one, leaving room for a phrase it had already
           decided to drop. Alignment does not affect the widths being summed. */
        t.classList.add('ticker--fit');

        var avail = t.clientWidth;
        var used = 0;
        var keep = -1;

        for (var i = 0; i < items.length; i++) {
          var w = items[i].getBoundingClientRect().width;
          if (used + w > avail + 0.5) { break; }
          used += w;
          keep = i;
        }

        /* Never end on a separator dot. */
        while (keep >= 0 && items[keep].textContent.trim() === DOT) { keep--; }

        /* If not even the first phrase fits, keep it and let CSS clip it. A
           blank blue bar would be worse than a clipped word. */
        if (keep < 0) { keep = 0; }

        for (var j = keep + 1; j < items.length; j++) { items[j].style.display = 'none'; }
      });
    }

    var t = null;
    function schedule() {
      if (t) { clearTimeout(t); }
      t = setTimeout(fit, 120);
    }

    fit();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    if (mq) {
      if (mq.addEventListener) { mq.addEventListener('change', fit); }
      else if (mq.addListener) { mq.addListener(fit); }
    }
  }

  /* -------------------------------------------------------------------------
     5. DELIVERY CHOICE
     Step 1 sends the shopper to a different domain, so this is the last thing
     we can measure on our own site. Reports which method was chosen.

     delivery_method comes from the block's Analytics key setting, NOT from the
     visible label. The label is translated, so keying on it would report the
     English and Spanish pages as two different conversions.

     FAIL OPEN, deliberately. No preventDefault, no navigation blocking, no
     timeout before the redirect. This is the single most important link on the
     site, and an under counted micro conversion is a far cheaper failure than a
     dead Start Your Rental button. Google Tag Manager should own the race, via
     a Click, Just Links trigger with Wait for Tags on. See
     docs/conversion-tracking.md.
  ------------------------------------------------------------------------- */
  function initDeliveryChoice() {
    if (document.documentElement.dataset.pmcDelivery) { return; }
    document.documentElement.dataset.pmcDelivery = '1';

    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('[data-pmc-method]') : null;
      if (!a) { return; }

      var payload = {
        delivery_method: a.getAttribute('data-pmc-method') || 'other',
        delivery_label: (a.querySelector('.s1-choice-t') || a).textContent.trim(),
        page_path: window.location.pathname,
        page_section: 'band_rental_step_1'
      };

      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'rental_delivery_select', payload);
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: 'rental_delivery_select' }, payload));
      } catch (err) { /* never let analytics take the link down */ }
    });
  }

  /* ----------------------------------------------------------------------- */
  function init(root) {
    initVideo(root || document);
    initStickyBar();
    initTracking();
    initTickerFit();
    initDeliveryChoice();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme customizer re-renders a section on edit. Re-init that subtree. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();